// Web push registration. Metro resolves notifications.native.ts in place of this
// file for the iOS/Android build, so the web Firebase messaging SDK is never
// pulled into the native bundle. Ported from meditation_group/web, dropping the
// legacy-WebView bridge (Knexo runs as a real native app, not a WebView shell).
import {
  getMessaging,
  getToken,
  isSupported,
  onMessage,
} from "firebase/messaging";
import app from "../firebase";
import { setToken } from "./fcmTokens";

// Re-exported so callers can import setToken from the same module on both
// platforms (notifications.native.ts re-exports it too).
export { setToken } from "./fcmTokens";

// Master kill-switch for browser push. Native is unaffected by this flag.
export const WEB_PUSH_ENABLED = true;

// Web Push certificate key pair for the meldpot Firebase project.
// Firebase Console -> Project Settings -> Cloud Messaging -> Web configuration
// -> "Web Push certificates". This is the public half of the pair — it is handed
// to the browser as the applicationServerKey, so it is not a secret.
const VAPID_KEY =
  "BKKU727_RsJKzxPgBES5GZEb14Lk0wyYT_zOKvG6Lt3U4KU-nm_oR5OIqV54hu4CJw4xNMfrBv88tC8bKZouyLU";

const SW_URL = "/firebase-messaging-sw.js";

let messagingInstance: ReturnType<typeof getMessaging> | null = null;
const getMessagingInstance = () => {
  if (typeof window === "undefined") return null;
  if (messagingInstance) return messagingInstance;
  try {
    messagingInstance = getMessaging(app);
  } catch {
    return null;
  }
  return messagingInstance;
};

const registerServiceWorker = async (): Promise<ServiceWorkerRegistration> => {
  const existing = await navigator.serviceWorker.getRegistration(SW_URL);
  if (existing) return existing;
  return navigator.serviceWorker.register(SW_URL);
};

/**
 * True when we should offer the in-app "enable notifications" prompt — i.e. the
 * browser has not yet been asked. Used by EnableNotificationsPrompt.
 */
export const shouldOfferNotificationPrompt = async (): Promise<boolean> => {
  if (!WEB_PUSH_ENABLED) return false;
  if (typeof Notification === "undefined") return false;
  return Notification.permission === "default";
};

const getNotificationToken = async (): Promise<string | null> => {
  if (!WEB_PUSH_ENABLED) return null;
  if (typeof window === "undefined") return null;
  if (!(await isSupported())) return null;

  if (!VAPID_KEY) {
    console.warn("[Notifications] Missing VAPID key; web push disabled.");
    return null;
  }

  const messaging = getMessagingInstance();
  if (!messaging) return null;

  if (Notification.permission === "denied") return null;
  if (Notification.permission === "default") {
    const result = await Notification.requestPermission();
    if (result !== "granted") return null;
  }

  const swRegistration = await registerServiceWorker();
  return getToken(messaging, {
    vapidKey: VAPID_KEY,
    serviceWorkerRegistration: swRegistration,
  });
};

/**
 * Request permission if needed, then persist the browser push token. Triggered
 * by an explicit user action (the enable-notifications prompt).
 */
export const registerPushToken = async (userId: string): Promise<void> => {
  if (!userId) return;
  try {
    const token = await getNotificationToken();
    if (!token) return;
    await setToken(token, userId, "web");
    listenForForegroundMessages();
  } catch (error) {
    console.error("[Notifications] Failed to register web push token:", error);
  }
};

/**
 * Persist the token only if permission was already granted — never prompts.
 * Called on launch so a returning user's token stays fresh.
 */
export const registerPushTokenSilently = async (
  userId: string,
): Promise<void> => {
  if (typeof Notification === "undefined") return;
  if (Notification.permission !== "granted") return;
  await registerPushToken(userId);
};

let foregroundListenerActive = false;

const listenForForegroundMessages = () => {
  if (foregroundListenerActive) return;
  const messaging = getMessagingInstance();
  if (!messaging) return;
  foregroundListenerActive = true;

  onMessage(messaging, async (payload) => {
    const title =
      (payload.data?.title as string) ?? payload.notification?.title ?? "Knexo";
    const body =
      (payload.data?.body as string) ?? payload.notification?.body ?? "";
    const clickAction =
      (payload.data && (payload.data.clickAction as string)) || undefined;
    const data = clickAction ? { clickAction } : payload.data;

    try {
      const swRegistration =
        await navigator.serviceWorker.getRegistration(SW_URL);
      if (swRegistration) {
        await swRegistration.showNotification(title, { body, data });
        return;
      }
    } catch (error) {
      console.error(
        "[Notifications] Failed to show foreground notification:",
        error,
      );
    }

    if (typeof Notification !== "undefined") {
      new Notification(title, { body });
    }
  });
};
